import React, { useEffect, useRef, useState, useId, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { modalStack, MODAL_TIMINGS } from './modalStack';

export interface KlMenuItem {
    id: string;
    label: string;
    icon?: React.ReactNode;
    iconName?: string;
    iconColor?: string;
    onClick: () => void;
    disabled?: boolean;
}

export interface KlMenuProps {
    open: boolean;
    onClose: () => void;
    anchorRef?: React.RefObject<HTMLElement | null>;
    items?: KlMenuItem[];
    children?: React.ReactNode;
    id?: string;
}

export const KlMenu: React.FC<KlMenuProps> = ({
    open,
    onClose,
    anchorRef,
    items,
    children,
    id: propId
}) => {
    const autoId = useId();
    const menuId = propId || `kl-menu-${autoId}`;

    const [isMounted, setIsMounted] = useState(false);
    const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'open' | 'closing'>('closed');
    const [anchorPos, setAnchorPos] = useState<{ top: number; originX: number } | null>(null);

    const menuRef = useRef<HTMLDivElement>(null);
    const prevActiveElementRef = useRef<HTMLElement | null>(null);
    const didUnmountRef = useRef(false);

    const prefersReducedMotion = typeof window !== 'undefined'
        ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
        : false;

    const cleanupAndUnmount = useCallback(() => {
        if (didUnmountRef.current) return;
        didUnmountRef.current = true;
        setIsMounted(false);
        setAnimationState('closed');
    }, []);

    const updateAnchorPos = useCallback(() => {
        if (anchorRef?.current) {
            const rect = anchorRef.current.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0 && rect.bottom > 0) {
                setAnchorPos({
                    top: rect.bottom + 6,
                    originX: Math.max(16, Math.min(window.innerWidth - 32, rect.left + rect.width / 2 - 12))
                });
                return;
            }
        }
        setAnchorPos(prev => prev ?? {
            top: 86,
            originX: window.innerWidth / 2 - 12
        });
    }, [anchorRef]);

    useEffect(() => {
        if (open) {
            didUnmountRef.current = false;
            prevActiveElementRef.current = document.activeElement as HTMLElement | null;
            updateAnchorPos();
            setIsMounted(true);

            const unregister = modalStack.push({
                id: menuId,
                type: 'menu',
                onClose,
                returnFocusElement: prevActiveElementRef.current
            });

            setAnimationState('opening');
            const timer = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setAnimationState('open');
                });
            });

            return () => {
                cancelAnimationFrame(timer);
                unregister();
            };
        } else if (isMounted) {
            setAnimationState('closing');

            const durationMs = prefersReducedMotion
                ? MODAL_TIMINGS.reducedMotion.fadeMs
                : MODAL_TIMINGS.dialog.fadeMs;

            const safetyTimer = setTimeout(() => {
                cleanupAndUnmount();
            }, durationMs + MODAL_TIMINGS.safetyMarginMs);

            return () => clearTimeout(safetyTimer);
        }
    }, [open, menuId, onClose, isMounted, prefersReducedMotion, cleanupAndUnmount, updateAnchorPos]);

    // Recalcular posición si cambia el tamaño de ventana o scroll
    useEffect(() => {
        if (!isMounted || !open) return;
        const handleResize = () => {
            // Evitar recalcular si el resize es una reducción brusca de altura típica de apertura de teclado virtual
            if (window.visualViewport && window.visualViewport.height < window.innerHeight * 0.7) {
                return;
            }
            updateAnchorPos();
        };
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize, true);
        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('scroll', handleResize, true);
        };
    }, [isMounted, open, updateAnchorPos]);

    if (!isMounted) return null;

    const topPos = anchorPos ? anchorPos.top : 86;
    const originX = anchorPos ? `${anchorPos.originX}px` : '50%';
    const transformOrigin = `${originX} -6px`;

    const isOpen = animationState === 'open';
    const isClosing = animationState === 'closing' || animationState === 'opening';

    return createPortal(
        <div
            id={menuId}
            ref={menuRef}
            data-kl-menu="true"
            data-kl-modal-open={isOpen ? 'true' : 'false'}
            role="menu"
            aria-modal="true"
            className="fixed inset-0 z-[120]"
        >
            {/* Velo --kl-scrim sin desenfoque */}
            <div
                className="fixed inset-0 transition-opacity"
                style={{
                    backgroundColor: 'var(--kl-scrim, rgba(15, 23, 42, 0.38))',
                    opacity: isOpen ? 1 : 0,
                    transitionDuration: prefersReducedMotion ? '120ms' : '180ms',
                    transitionTimingFunction: isOpen
                        ? 'var(--kl-ease-out, cubic-bezier(.32, .72, 0, 1))'
                        : 'var(--kl-ease-in, cubic-bezier(.4, 0, 1, 1))'
                }}
                onClick={onClose}
            />

            {/* Menú desplegable */}
            <div
                className="fixed left-3 right-3 bg-white dark:bg-slate-900 rounded-[14px] p-[6px] flex flex-col gap-[6px] overflow-y-auto overscroll-contain"
                style={{
                    top: `${topPos}px`,
                    maxHeight: `calc(100vh - ${topPos}px - 16px - var(--kl-safe-bottom, env(safe-area-inset-bottom, 0px)))`,
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.16)',
                    transformOrigin,
                    transform: isOpen
                        ? 'scale(1) translateY(0)'
                        : 'scale(0.96) translateY(-6px)',
                    opacity: isOpen ? 1 : 0,
                    transition: prefersReducedMotion
                        ? `opacity 120ms ease`
                        : `transform var(--kl-dur-fade, 180ms) ${isOpen ? 'var(--kl-ease-out, cubic-bezier(.32, .72, 0, 1))' : 'var(--kl-ease-in, cubic-bezier(.4, 0, 1, 1))'}, opacity var(--kl-dur-fade, 180ms) ${isOpen ? 'var(--kl-ease-out, cubic-bezier(.32, .72, 0, 1))' : 'var(--kl-ease-in, cubic-bezier(.4, 0, 1, 1))'}`,
                    zIndex: 1
                }}
                onTransitionEnd={(e) => {
                    if (e.target === e.currentTarget && animationState === 'closing') {
                        cleanupAndUnmount();
                    }
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {items ? (
                    items.map((item) => (
                        <button
                            key={item.id}
                            type="button"
                            disabled={item.disabled}
                            onClick={() => {
                                onClose();
                                item.onClick();
                            }}
                            className="h-[48px] min-h-[48px] px-3 rounded-[12px] bg-[#F8FAFC] dark:bg-slate-800/80 flex items-center gap-3 text-[14px] font-semibold text-[#0F172A] dark:text-slate-100 hover:bg-slate-100 dark:hover:bg-slate-800 active:scale-[0.99] transition-all text-left border-0 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {item.icon ? (
                                item.icon
                            ) : item.iconName ? (
                                <span
                                    className={`material-symbols-outlined text-[20px] shrink-0 ${item.iconColor || 'text-slate-600 dark:text-slate-300'}`}
                                >
                                    {item.iconName}
                                </span>
                            ) : null}
                            <span className="flex-1 truncate">{item.label}</span>
                        </button>
                    ))
                ) : (
                    children
                )}
            </div>
        </div>,
        document.body
    );
};
